// Katalog — xsib-core `catalog` moduli bilan haqiqiy kontrakt.
// Manba: `catalog/internal/PlanController.java`, `PolicyController.java`.
//
// ⚠️ YO'L PREFIKSI `/api/catalog/...`, `/api/plans` EMAS. Oxirgisi mavjud
// emas va (xavfsizlik sozlamasi tufayli) 404 emas, TANASIZ 401 qaytaradi —
// ya'ni noto'g'ri yo'l "sessiya tugadi" bo'lib ko'rinardi.
//
// ⚠️ PLAN ≠ POLICY (01-DOMAIN-MODEL.md §1):
//   Plan   — SOTUV birligi: narx, kvota, amal muddati
//   Policy — TEXNIK cheklov: tezlik, timeout, RADIUS atributlari
// Bitta Policy bir necha Plan'da ishlatiladi, shuning uchun ular bitta
// sahifada birlashtirilmaydi. Va eng muhimi: `plan.policyId` NULL BO'LISHI
// MUMKIN — "faqat kvota beradigan paket" (tezlikka tegmaydi, mavjud
// siyosat kuchda qoladi).
//
// ⚠️ LEKIN `policyId === null` NIYATNI AYTMAYDI — 2026-08-13 da jonli
// tizimda o'lchandi. Ikki BUTUNLAY boshqa holat bazada ham, bu yerda ham
// aynan bir xil ko'rinardi:
//
//   qo'shimcha paket (ataylab)   policyId=null, PREPAID_TRAFFIC, quota>0
//   asosiy tarif (UNUTILGAN)     policyId=null, PREPAID_TRAFFIC, quota>0
//
// Natijasi: `tarif-15m` ACTIVE holatda sotuvda turdi, unga 4 ta grant
// berildi, abonent `firdavs5` esa RADIUS'dan "Tarif biriktirilmagan"
// javobini oldi — sotib oldi va umuman ulana olmadi. Panel esa ikkalasiga
// ham bitta ko'k «Faqat kvota» tegini chizardi, ya'ni operator xatoni
// KO'RA OLMASDI.
//
// Endi niyat alohida maydonda: `addon` (operator aytadi), `misconfigured`
// va `sellable` esa backend hisoblab beradi (`Plan.isMisconfigured()`,
// `Plan.isSellable()`, migratsiya `V202608153100`).

import { z } from 'zod'
import { api } from '@/shared/api/client'
import { requireEveryField } from '@/shared/api/fullReplace'
import { instantSchema, moneySchema, planSellable, toMoney, uuidSchema } from '@/shared/api/schemas'
// ⚠️ `money` FUNKSIYASI import QILINMAYDI — faqat tipi. `shared/lib/money`
// `app/i18n` ni tortadi, u esa yuklanishda `document` ga tegadi: shu bitta
// import katalog kontraktini brauzer bootstrap'iga bog'lab qo'yardi.
import type { Money } from '@/shared/lib/money'
import { toNameI18n, type NameLanguage } from '@/shared/lib/planName'
import {
  TIME_WINDOW_KINDS,
  timeWindowDrafts,
  type TimeWindow,
  type TimeWindowFormRow,
} from './timeWindows'

export const BILLING_KINDS = [
  'PREPAID_TRAFFIC',
  'PREPAID_TIME',
  'UNLIMITED',
  'POSTPAID',
] as const
export type BillingKind = (typeof BILLING_KINDS)[number]

export const DELIVERY_METHODS = ['PPPOE', 'HOTSPOT', 'STATIC'] as const
export type DeliveryMethod = (typeof DELIVERY_METHODS)[number]

export const PLAN_STATES = ['DRAFT', 'ACTIVE', 'ARCHIVED'] as const
export type PlanState = (typeof PLAN_STATES)[number]

export const POLICY_STATES = ['ACTIVE', 'ARCHIVED'] as const
export type PolicyState = (typeof POLICY_STATES)[number]

// --- Plan -------------------------------------------------------------------

/*
  ⚠️ UCHALA YANGI MAYDON HAM `nullish()` — ATAYLAB.

  Panel va backend alohida chiqariladi (§25). Eski build turgan bir necha
  daqiqada bu maydonlar javobda UMUMAN bo'lmaydi; `z.boolean()` bo'lsa
  `zod` butun ro'yxatni rad etardi va Paketlar sahifasi BO'SH ochilardi —
  ya'ni "siyosatsiz tarifni ko'rsatamiz" degan tuzatish o'zi sahifani
  o'ldirardi. Maydon yo'qligining oqibati `toPlan` da aniq yozilgan.
*/
const planSchema = z.object({
  id: uuidSchema,
  tenantId: uuidSchema,
  /** ⚠️ NULL bo'lishi MUMKIN — "faqat kvota beradigan paket" */
  policyId: uuidSchema.nullish(),
  /** `true` = qo'shimcha hajm/vaqt paketi, siyosat ATAYLAB yo'q */
  addon: z.boolean().nullish(),
  /** Backend hisoblaydi: `policyId == null && !addon` — siyosat UNUTILGAN */
  misconfigured: z.boolean().nullish(),
  /** Backend hisoblaydi: `state == ACTIVE && !misconfigured` */
  sellable: z.boolean().nullish(),
  name: z.string(),
  /** Nom tarjimalari. `nullish` — eski backendda maydon UMUMAN yo'q (§25) */
  nameI18n: z.record(z.string(), z.string()).nullish(),
  billingKind: z.enum(BILLING_KINDS),
  price: moneySchema,
  quotaBytes: z.number().nullish(),
  quotaSeconds: z.number().nullish(),
  validityDays: z.number().nullish(),
  nextPlanId: uuidSchema.nullish(),
  deliveryMethod: z.enum(DELIVERY_METHODS),
  isPublic: z.boolean().nullish(),
  state: z.enum(PLAN_STATES),
  createdAt: instantSchema.nullish(),
  updatedAt: instantSchema.nullish(),
})

type PlanRaw = z.infer<typeof planSchema>

export interface Plan {
  id: string
  policyId: string | null
  /** Qo'shimcha hajm/vaqt paketi — siyosat ATAYLAB yo'q */
  addon: boolean
  /** Siyosat kutilardi, lekin yo'q — bu paket sotilmaydi */
  misconfigured: boolean
  /** Sotuvga tayyor: ACTIVE va nomuvofiq emas */
  sellable: boolean
  name: string
  /** Nom tarjimalari; bo'sh obyekt = tarjima kiritilmagan */
  nameI18n: Record<string, string>
  billingKind: BillingKind
  price: Money
  quotaBytes: number | null
  quotaSeconds: number | null
  validityDays: number | null
  nextPlanId: string | null
  deliveryMethod: DeliveryMethod
  isPublic: boolean
  state: PlanState
  createdAt: string | null
}

function toPlan(raw: PlanRaw): Plan {
  return {
    id: raw.id,
    policyId: raw.policyId ?? null,
    addon: raw.addon ?? false,
    // ⚠️ Maydon kelmasa (eski backend) "nomuvofiq EMAS" deymiz. Teskarisi —
    // `?? true` — har bir paketni qizil qilib ko'rsatardi va ogohlantirish
    // ma'nosini yo'qotardi. Bu yerda taxmin qilib ham bo'lmaydi:
    // `policyId === null` dan xulosa chiqarish AYNAN o'sha chalkashlik.
    misconfigured: raw.misconfigured ?? false,
    // ⚠️ Qoida `shared/api/schemas` da — u `reference.ts` bilan BITTA
    // bo'lishi shart, aks holda sotuv ro'yxati eski nusxada qolib ketardi.
    sellable: planSellable(raw),
    name: raw.name,
    nameI18n: raw.nameI18n ?? {},
    billingKind: raw.billingKind,
    price: toMoney(raw.price),
    quotaBytes: raw.quotaBytes ?? null,
    quotaSeconds: raw.quotaSeconds ?? null,
    validityDays: raw.validityDays ?? null,
    nextPlanId: raw.nextPlanId ?? null,
    deliveryMethod: raw.deliveryMethod,
    isPublic: raw.isPublic ?? true,
    state: raw.state,
    createdAt: raw.createdAt ?? null,
  }
}

/** Bitta plan javobini o'qish — `createPlan`/`updatePlan`/`archivePlan` uchun bitta yo'l. */
export function parsePlan(raw: unknown): Plan {
  return toPlan(planSchema.parse(raw))
}

export async function fetchPlans(): Promise<Plan[]> {
  const response = await api.get<unknown>('/api/catalog/plans')
  return z.array(planSchema).parse(response.data).map(toPlan)
}

/**
 * Paket ro'yxatida siyosat ustuni nima ko'rsatishi.
 *
 * ⚠️ Bu funksiya JSX dan TASHQARIDA turadi va bu ataylab: qaror shu yerda
 * sinaladi (`tests/catalog-plan.mjs`), sahifada esa faqat chizish qoladi.
 *
 * Uch holat, ikkita emas:
 *   `MISCONFIGURED` — siyosat UNUTILGAN. Paket sotilmaydi va u bilan
 *                     abonent ulana olmaydi (jonli `tarif-15m` holati).
 *   `ADDON`         — siyosat ATAYLAB yo'q: qo'shimcha hajm/vaqt paketi.
 *   `POLICY`        — siyosat bor, nomi ko'rsatiladi.
 *
 * ⚠️ `policyId === null && !addon` bu yerda TAKRORLANMAYDI: qaror
 * backendniki (`Plan.isMisconfigured()`), panel esa uni faqat ko'rsatadi.
 * Ikki joyda ikki xil qoida yozilsa, ular ertami-kechmi ajralib ketardi.
 *
 * ⚠️ `policyId === null` holati baribir `ADDON` ga tushadi — bu ESKI
 * backend uchun: u `addon` ni umuman yubormaydi va bunday paket bugungi
 * ko'k teg bilan qolishi kerak, bo'sh katak bilan emas.
 */
export type PlanPolicyBadge = 'MISCONFIGURED' | 'ADDON' | 'POLICY'

/**
 * ⚠️ ARXIVLANGAN PAKET «BUZUQ» DEB BELGILANMAYDI.
 *
 * Backend `misconfigured` ni HOLATGA QARAMASDAN hisoblaydi
 * (`Plan.isMisconfigured()` — faqat `policyId == null && !addon`), va bu
 * server tomonda to'g'ri: `isSellable()` allaqachon `state == ACTIVE` ni
 * ham talab qiladi, ya'ni arxivlangan paket baribir sotilmaydi.
 *
 * Lekin PANELDA qizil teg va sahifa banneri boshqa narsani anglatadi:
 * «harakat qiling — siyosat biriktiring». Arxivlangan paket uchun bunday
 * harakat YO'Q va bo'lishi ham kerak emas: u sotuvdan ataylab chiqarilgan.
 *
 * O'lchandi (Staging, 2026-08-16): `ZZ-qa-nopolicy` — `state=ARCHIVED`,
 * `misconfigured=true`. Sahifa tepasida «1 plans have no policy. They are
 * off sale…» banneri turardi va uni o'chirishning YAGONA yo'li o'chirilgan
 * paketni qayta tirgizish bo'lardi. Ogohlantirish shu tariqa doimiy
 * shovqinga aylanadi va u bilan birga HAQIQIY buzuq paket ham e'tibordan
 * qoladi.
 *
 * ⚠️ `state` maydoni IXTIYORIY o'qiladi: mavjud chaqiruvchilar (va
 * `tests/catalog-plan.mjs` dagi 8 ta tekshiruv) uni bermaydi, va berilmasa
 * xulq AVVALGIDEK qoladi — «arxivlangan emas» deb o'qiladi.
 */
export function planPolicyBadge(
  plan: Pick<Plan, 'policyId' | 'addon' | 'misconfigured'> & { state?: PlanState },
): PlanPolicyBadge {
  if (plan.misconfigured) {
    // ⚠️ Arxivlangan buzuq paket `ADDON` GA TUSHIB KETMASLIGI kerak:
    // ko'k «Faqat kvota» tegi YOLG'ON bo'lardi (siyosat ataylab emas,
    // unutib qoldirilgan). `POLICY` shoxida ustun oddiy «—» chizadi —
    // ogohlantirmaydi, lekin yolg'on ham aytmaydi.
    return plan.state === 'ARCHIVED' ? 'POLICY' : 'MISCONFIGURED'
  }
  if (plan.addon || plan.policyId === null) return 'ADDON'
  return 'POLICY'
}

/**
 * Sahifa tepasidagi ogohlantirish uchun — nechta paket sotuvdan chiqib qolgan.
 *
 * ⚠️ Arxivlanganlar KIRMAYDI ({@link planPolicyBadge} javadoc'i): sanoq
 * «nechta paketni tuzatish kerak» degan savolga javob beradi, arxivdagi
 * paketni esa tuzatish kerak emas.
 */
export function misconfiguredPlans(plans: readonly Plan[]): Plan[] {
  return plans.filter((plan) => planPolicyBadge(plan) === 'MISCONFIGURED')
}

/**
 * ⚠️ HAR MAYDON MAJBURIY — `PUT /api/catalog/plans/{id}` TO'LIQ ALMASHTIRADI.
 *
 * `JdbcPlanRepository.update` bitta `UPDATE` da har ustunga qiymat yozadi
 * va `COALESCE` yo'q, ya'ni yuborilmagan maydon bazada JIMGINA tozalanadi.
 * Ixtiyoriy (`?`) maydon shu tozalashni tip tekshiruvidan o'tkazib
 * yuborardi — shuning uchun bu yerda ixtiyoriy maydon YO'Q.
 */
export interface PlanDraft {
  name: string
  /** Nom tarjimalari; bo'sh qiymatlar backendda tashlanadi */
  nameI18n: Record<string, string>
  policyId: string | null
  /**
   * Bu qo'shimcha hajm/vaqt paketimi — ya'ni siyosat ATAYLAB yo'qmi.
   *
   * ⚠️ Backend `addon = true` bilan `policyId` ni BIRGA qabul qilmaydi
   * (400), teskarisi ham: `addon = false` da `policyId` MAJBURIY.
   */
  addon: boolean
  billingKind: BillingKind
  price: Money
  quotaBytes: number | null
  quotaSeconds: number | null
  validityDays: number | null
  nextPlanId: string | null
  deliveryMethod: DeliveryMethod
  isPublic: boolean
  state: PlanState
}

/** Serverdagi `PlanDraft.java` record'i bilan solishtiriladigan ro'yxat. */
export const PLAN_DRAFT_FIELDS: readonly (keyof PlanDraft)[] = [
  'name',
  'nameI18n',
  'policyId',
  'addon',
  'billingKind',
  'price',
  'quotaBytes',
  'quotaSeconds',
  'validityDays',
  'nextPlanId',
  'deliveryMethod',
  'isPublic',
  'state',
]

/** `PlanFormModal` beradigan shakl — quruvchi CHAQIRIB o'lchanishi uchun shu yerda. */
export interface PlanFormValues {
  name: string
  nameI18n?: Partial<Record<NameLanguage, string>>
  policyId?: string | null
  addon: boolean
  billingKind: BillingKind
  price: Money | null
  /** Operator GB'da o'ylaydi, baza baytda saqlaydi */
  quotaGb?: number | null
  quotaSeconds?: number | null
  validityDays?: number | null
  deliveryMethod: DeliveryMethod
  isPublic: boolean
  state: PlanState
}

const BYTES_IN_GB = 1024 ** 3

function gbToBytes(gb: number | null | undefined): number | null {
  return gb === null || gb === undefined ? null : Math.round(gb * BYTES_IN_GB)
}

/**
 * So'rov tanasi — FUNKSIYA, chunki qorovul uni CHAQIRIB o'lchaydi.
 *
 * ⚠️ `nextPlanId` FORMADA YO'Q va bu qaror: «muddat tugagach avtomatik
 * o'tish» zanjirini majburlaydigan kod backendda yo'q — faqat
 * `GET /{id}/next-chain` o'qiydi. Maydonni formaga chiqarish sodir
 * bo'lmaydigan xulqni va'da qilardi. Lekin ustunda HAQIQIY ma'lumot
 * turishi mumkin (zanjir bazada quriladi, sikl `policy_no_cycle` bilan
 * taqiqlangan), shuning uchun joriy qiymat QAYTARILADI.
 */
export function planDraftBody(
  values: PlanFormValues,
  plan: Plan | null,
  fallbackPrice: Money,
): PlanDraft {
  return {
    name: values.name.trim(),
    nameI18n: toNameI18n(values.nameI18n ?? {}),
    policyId: values.addon ? null : (values.policyId ?? null),
    addon: values.addon,
    billingKind: values.billingKind,
    price: values.price ?? fallbackPrice,
    quotaBytes: gbToBytes(values.quotaGb),
    quotaSeconds: values.quotaSeconds ?? null,
    validityDays: values.validityDays ?? null,
    nextPlanId: plan?.nextPlanId ?? null,
    deliveryMethod: values.deliveryMethod,
    isPublic: values.isPublic,
    state: values.state,
  }
}

export async function createPlan(draft: PlanDraft): Promise<Plan> {
  requireEveryField(draft, PLAN_DRAFT_FIELDS, 'paket')
  const response = await api.post<unknown>('/api/catalog/plans', draft)
  return parsePlan(response.data)
}

export async function updatePlan(id: string, draft: PlanDraft): Promise<Plan> {
  requireEveryField(draft, PLAN_DRAFT_FIELDS, 'paket')
  const response = await api.put<unknown>(`/api/catalog/plans/${id}`, draft)
  return parsePlan(response.data)
}

/**
 * ⚠️ O'CHIRMAYDI — ARXIVLAYDI (`DELETE` → `catalog.archivePlan`).
 * Sotilgan paket tarixi qolishi kerak, aks holda eski grantlar "noma'lum
 * plan" ga ishora qilardi.
 */
export async function archivePlan(id: string): Promise<Plan> {
  const response = await api.delete<unknown>(`/api/catalog/plans/${id}`)
  return parsePlan(response.data)
}

// --- Policy -----------------------------------------------------------------

const timeWindowSchema = z.object({
  kind: z.enum(TIME_WINDOW_KINDS),
  days: z.array(z.number()),
  from: z.string(),
  to: z.string(),
  rateLimit: z.string().nullish(),
  burst: z.string().nullish(),
})

const policySchema = z.object({
  id: uuidSchema,
  tenantId: uuidSchema,
  name: z.string(),
  nameI18n: z.record(z.string(), z.string()).nullish(),
  /** MikroTik shakli: "50M/50M" */
  rateLimit: z.string().nullish(),
  burst: z.string().nullish(),
  quotaBytes: z.number().nullish(),
  dailyQuotaBytes: z.number().nullish(),
  sessionTimeout: z.number().nullish(),
  idleTimeout: z.number().nullish(),
  simultaneousUse: z.number().nullish(),
  fupPolicyId: uuidSchema.nullish(),
  attributes: z.record(z.string(), z.string()).default({}),
  state: z.enum(POLICY_STATES),
  timeWindows: z.array(timeWindowSchema).nullish(),
  createdAt: instantSchema.nullish(),
  updatedAt: instantSchema.nullish(),
})

type PolicyRaw = z.infer<typeof policySchema>

/** ⚠️ Backenddagi `PolicyDraft.INTERIM_ATTRIBUTE` bilan AYNAN bir xil. */
const INTERIM_ATTRIBUTE = 'Acct-Interim-Interval'

export interface Policy {
  id: string
  name: string
  /** Nom tarjimalari; bo'sh obyekt = tarjima kiritilmagan */
  nameI18n: Record<string, string>
  rateLimit: string | null
  burst: string | null
  quotaBytes: number | null
  dailyQuotaBytes: number | null
  sessionTimeout: number | null
  idleTimeout: number | null
  simultaneousUse: number | null
  /**
   * Trafik hisoboti oralig'i, soniya.
   *
   * ⚠️ Backend uni ALOHIDA ustunda saqlamaydi — u `attributes` ichida,
   * `Acct-Interim-Interval` kaliti bilan. Sabab: bu oddiy RADIUS
   * atributi va zanjir (`PolicyAttributes` → authorize) allaqachon
   * ishlaydi; ustun qo'shish faqat migratsiya talab qilardi, natija
   * esa bir xil bo'lardi.
   *
   * `null` = ko'rsatilmagan, tizim qiymati (60 s) ishlatiladi.
   */
  interimInterval: number | null
  fupPolicyId: string | null
  attributes: Readonly<Record<string, string>>
  state: PolicyState
  timeWindows: TimeWindow[]
}

function toPolicy(raw: PolicyRaw): Policy {
  return {
    id: raw.id,
    name: raw.name,
    nameI18n: raw.nameI18n ?? {},
    rateLimit: raw.rateLimit ?? null,
    burst: raw.burst ?? null,
    quotaBytes: raw.quotaBytes ?? null,
    dailyQuotaBytes: raw.dailyQuotaBytes ?? null,
    sessionTimeout: raw.sessionTimeout ?? null,
    idleTimeout: raw.idleTimeout ?? null,
    simultaneousUse: raw.simultaneousUse ?? null,
    // ⚠️ Atributdan chiqariladi. Son bo'lmagan qiymat (qo'lda buzilgan
    // yoki bo'sh) `null` ga aylanadi — `NaN` shaklga tushib, `InputNumber`
    // ni jimgina buzardi.
    interimInterval: Number.isFinite(Number(raw.attributes[INTERIM_ATTRIBUTE]))
      && raw.attributes[INTERIM_ATTRIBUTE] !== undefined
      ? Number(raw.attributes[INTERIM_ATTRIBUTE])
      : null,
    fupPolicyId: raw.fupPolicyId ?? null,
    attributes: raw.attributes,
    state: raw.state,
    timeWindows: (raw.timeWindows ?? []).map((window) => ({
      ...window,
      rateLimit: window.rateLimit ?? null,
      burst: window.burst ?? null,
    })),
  }
}

export async function fetchPolicies(): Promise<Policy[]> {
  const response = await api.get<unknown>('/api/catalog/policies')
  return z.array(policySchema).parse(response.data).map(toPolicy)
}

/**
 * ⚠️ HAR MAYDON MAJBURIY — sabab {@link PlanDraft} da, shakl aynan bir xil
 * (`JdbcPolicyRepository.update`, `COALESCE` yo'q).
 */
export interface PolicyDraft {
  name: string
  /** Nom tarjimalari; bo'sh qiymatlar backendda tashlanadi */
  nameI18n: Record<string, string>
  rateLimit: string | null
  burst: string | null
  quotaBytes: number | null
  dailyQuotaBytes: number | null
  sessionTimeout: number | null
  idleTimeout: number | null
  simultaneousUse: number | null
  interimInterval: number | null
  fupPolicyId: string | null
  attributes: Record<string, string>
  state: PolicyState
  timeWindows: TimeWindow[]
}

/** Serverdagi `PolicyDraft.java` record'i bilan solishtiriladigan ro'yxat. */
export const POLICY_DRAFT_FIELDS: readonly (keyof PolicyDraft)[] = [
  'name',
  'nameI18n',
  'rateLimit',
  'burst',
  'quotaBytes',
  'dailyQuotaBytes',
  'sessionTimeout',
  'idleTimeout',
  'simultaneousUse',
  'interimInterval',
  'fupPolicyId',
  'attributes',
  'state',
  'timeWindows',
]

/** `PolicyFormModal` beradigan shakl. */
export interface PolicyFormValues {
  name: string
  nameI18n?: Partial<Record<NameLanguage, string>>
  rateLimit?: string
  burst?: string
  quotaGb?: number | null
  dailyQuotaGb?: number | null
  sessionTimeout?: number | null
  idleTimeout?: number | null
  simultaneousUse?: number | null
  interimInterval?: number | null
  fupPolicyId?: string | null
  state: PolicyState
  timeWindows?: TimeWindowFormRow[]
}

/**
 * Formadan tashqaridagi RADIUS atributlari — JORIY qiymatida qaytariladi.
 *
 * <h2>⚠️ Nega formaga CHIQARILMADI</h2>
 * Bu erkin kalit-qiymat to'plami va uning har kaliti abonentga
 * yuboriladigan RADIUS atributiga aylanadi, ya'ni tezlik va cheklovga
 * bevosita ta'sir qiladi. Erkin JSON tahrirlagich bitta xato belgida
 * tarmoq xulqini o'zgartirardi va tekshiradigan qoida yo'q — panel esa
 * `Mikrotik-Rate-Limit` singari atributlar uchun ATAYLAB shaklga
 * ega maydonlar beradi (`rateLimit`, `burst`, `interimInterval`).
 * Ko'rsatmaslik — ma'lumotni yo'qotish emas; TOZALASH — yo'qotish.
 *
 * <h2>⚠️ `Acct-Interim-Interval` xaritadan OLIB TASHLANADI</h2>
 * `PolicyDraft.attributesOrEmpty()` da qo'lda yozilgan atribut USTUN
 * turadi: `base.containsKey(INTERIM_ATTRIBUTE)` bo'lsa `interimInterval`
 * umuman qo'shilmaydi. Xaritani xomligicha qaytarish shu sababli
 * «Hisobot oralig'i» maydonini O'LIK qilardi — operator qiymatni
 * o'zgartiradi, saqlaydi, hech narsa o'zgarmaydi. Kalit shu yerda
 * tushiriladi va uni yagona egasi — formadagi maydon — qayta qo'yadi.
 */
function carriedAttributes(policy: Policy | null): Record<string, string> {
  const carried: Record<string, string> = {}
  for (const [key, value] of Object.entries(policy?.attributes ?? {})) {
    if (key !== INTERIM_ATTRIBUTE) carried[key] = value
  }
  return carried
}

export function policyDraftBody(values: PolicyFormValues, policy: Policy | null): PolicyDraft {
  return {
    name: values.name.trim(),
    nameI18n: toNameI18n(values.nameI18n ?? {}),
    // Bo'sh satr `null` ga aylantiriladi: backend naqshi bo'sh satrni
    // rad etardi va xato tanasiz 401 bo'lib kelardi
    rateLimit: values.rateLimit?.trim() || null,
    burst: values.burst?.trim() || null,
    quotaBytes: gbToBytes(values.quotaGb),
    dailyQuotaBytes: gbToBytes(values.dailyQuotaGb),
    sessionTimeout: values.sessionTimeout ?? null,
    idleTimeout: values.idleTimeout ?? null,
    simultaneousUse: values.simultaneousUse ?? null,
    interimInterval: values.interimInterval ?? null,
    fupPolicyId: values.fupPolicyId ?? null,
    attributes: carriedAttributes(policy),
    state: values.state,
    timeWindows: timeWindowDrafts(values.timeWindows),
  }
}

export async function createPolicy(draft: PolicyDraft): Promise<Policy> {
  requireEveryField(draft, POLICY_DRAFT_FIELDS, 'siyosat')
  const response = await api.post<unknown>('/api/catalog/policies', draft)
  return toPolicy(policySchema.parse(response.data))
}

export async function updatePolicy(id: string, draft: PolicyDraft): Promise<Policy> {
  requireEveryField(draft, POLICY_DRAFT_FIELDS, 'siyosat')
  const response = await api.put<unknown>(`/api/catalog/policies/${id}`, draft)
  return toPolicy(policySchema.parse(response.data))
}

export async function archivePolicy(id: string): Promise<Policy> {
  const response = await api.delete<unknown>(`/api/catalog/policies/${id}`)
  return toPolicy(policySchema.parse(response.data))
}

/**
 * Proyeksiya shu siyosatga qanday RADIUS atributlari yozishini ko'rsatadi.
 *
 * `reply` va `check` ALOHIDA: `Simultaneous-Use` va `Expiration` — check
 * tomonda (ular ruxsat berish shartlari), tezlik va timeout esa reply da
 * (ular javob atributlari). Bitta ro'yxatga qo'shish RADIUS mantiqini
 * yashirardi.
 */
const policyAttributesSchema = z.object({
  reply: z.record(z.string(), z.string()).default({}),
  check: z.record(z.string(), z.string()).default({}),
})

export type PolicyAttributes = z.infer<typeof policyAttributesSchema>

/**
 * ⚠️ `deliveryMethod` MAJBURIY so'rov parametri.
 *
 * Ilgari u yuborilmasdi va server har chaqiruvga 400 qaytarardi
 * (`PolicyController.attributes` da `@RequestParam DeliveryMethod` —
 * sukut qiymatisiz). Panelda bu jimgina ko'rinardi: RADIUS paneli
 * ochilar, ikkala ro'yxat esa BO'SH bo'lardi — ya'ni "bu siyosat hech
 * qanday atribut yozmaydi" degan YOLG'ON xulosa.
 *
 * Parametr ixtiyoriy ham bo'la olmaydi: atributlar to'plami usulga qarab
 * HAQIQATAN farq qiladi (`Session-Timeout` va `Mikrotik-Total-Limit`
 * faqat HOTSPOT'da chiqadi — `catalog.api.PolicyAttributes` jadvali).
 * Shuning uchun panelda usul TANLANADI va ekranda ko'rinib turadi.
 */
export async function fetchPolicyAttributes(
  id: string,
  deliveryMethod: DeliveryMethod,
): Promise<PolicyAttributes> {
  const response = await api.get<unknown>(`/api/catalog/policies/${id}/radius-attributes`, {
    params: { deliveryMethod },
  })
  return policyAttributesSchema.parse(response.data)
}
